import type {
  CollectionChallenge,
  ResourceChallenge,
  ResourceListTracker,
  ResourceLoginInfo,
} from '@dialogue/active-minutes'
import { put, take } from 'redux-saga/effects'
import { call, select } from 'typed-redux-saga'
import moment from 'moment'
import Toast from 'react-native-toast-message'
import { Linking } from 'react-native'
// Redux Actions
import ActiveMinutesActions, { ActiveMinutesTypes } from 'APP/Redux/ActiveMinutesRedux'

// Services
import Config from 'APP/Config'
import I18n from 'APP/I18n'
import { ActiveMinutes, ApiRequestError } from '@dialogue/services'
import { filterLatestConnections } from 'APP/Lib/ActiveMinutes/trackers/filters'
import { selectServiceFeatures } from 'APP/Store/Content/selectors'
import { SDK_TRACKERS, supportedTrackers } from 'APP/Lib/ActiveMinutes/trackers/constants'
import { CONNECTION_ERRORS } from 'APP/Lib/ActiveMinutes/trackers/errors'
import { initTerraConnection } from './TerraSagas'
import { categorizeChallenges } from 'APP/Lib/ActiveMinutes/challenges'
import { supportedTrackerActivities } from '../Lib/ActiveMinutes/trackers/constants'
import { navigationRef as Nav } from 'APP/Nav'
import { logger } from 'APP/Lib/Datadog'
import type { RootState } from 'APP/Store/CreateStore'
import type { TargetChangeType, TrackerName } from '@dialogue/services/dist/active-minutes/model'

export function* createActiveMinutes() {
  const accessToken = yield* select(selectAccessToken)

  // @ts-expect-error FIXME: require ACTIVE_MINUTES_SERVICE_URL in config, instead of allowing it to be optional
  return new ActiveMinutes.Service(accessToken, Config.ACTIVE_MINUTES_SERVICE_URL)
}

export const selectAccessToken = (state: RootState) => state.login.accessToken
export const selectTerraReferenceId = (state: RootState) =>
  state.activeMinutes.serviceLogin.terraReferenceId
export const connectedTrackersState = (state: RootState) => state.activeMinutes.connectedTrackers

export function* activeMinutesLogin() {
  try {
    const serviceFeatures = yield* select(selectServiceFeatures)
    const isEligible = serviceFeatures.fitness_trackers
    if (!isEligible) {
      yield put(ActiveMinutesActions.serviceLoginFailure(undefined, 'User is not elligible'))
      return
    }

    const ActiveMinutesService = yield* call(createActiveMinutes)
    // @ts-expect-error The @dialogue/active-minutes model is the source of truth
    const loginResponse: ResourceLoginInfo = yield* call(ActiveMinutesService.login)

    yield put(ActiveMinutesActions.serviceLoginSuccess(loginResponse.data.terra_reference_id))
  } catch (e) {
    logger.error('Failed to login with Active Minutes', {}, e)
    // @ts-expect-error FIXME: error is not guaranteed to be an Error instance
    // Consider if we really need to put anything in the store here, if not remove the action arg
    yield put(ActiveMinutesActions.serviceLoginFailure(e?.message || e?.name))
  }
}

export function* getActiveMinutes() {
  const from = moment().startOf('isoWeek').format(I18n.t('DateFormat'))
  const to = moment().endOf('isoWeek').format(I18n.t('DateFormat'))

  try {
    const ActiveMinutesService = yield* call(createActiveMinutes)

    const activeMinutes = yield* call(ActiveMinutesService.getActiveMinutes, {
      from,
      to,
    })

    yield put(ActiveMinutesActions.getActiveMinutesSuccess(activeMinutes))
  } catch (e: unknown) {
    const error = e as ApiRequestError
    yield put(ActiveMinutesActions.getActiveMinutesFailure())
    logger.error(
      'Failed to fetch Active Minutes data',
      { status: error?.code, url: error?.url, from, to },
      error
    )
  }
}

export function* getHistory({ from, weeksBackCount }: { from: string; weeksBackCount: number }) {
  try {
    const ActiveMinutesService = yield* call(createActiveMinutes)

    const history = yield* call(ActiveMinutesService.getHistory, {
      from,
      weeksBackCount,
    })

    yield put(ActiveMinutesActions.getHistorySuccess(history.slice().reverse()))
  } catch (e) {
    yield put(ActiveMinutesActions.getHistoryFailure())
    logger.error('Failed to fetch Active Minutes history', { from, weeksBackCount }, e)
  }
}

export function* updateWeeklyTarget({
  target,
  changeType = ActiveMinutes.Types.TargetChangeType.MANAUL,
}: {
  target: number
  changeType?: TargetChangeType
}) {
  try {
    const ActiveMinutesService = yield* call(createActiveMinutes)

    yield* call(ActiveMinutesService.setWeeklyTarget, {
      target,
      changeType,
    })

    yield put(ActiveMinutesActions.updateWeeklyTargetSuccess())
    yield put(ActiveMinutesActions.getActiveMinutes())

    if (changeType === ActiveMinutes.Types.TargetChangeType.ACCEPT_PROMPT) {
      const { activeMinutes } = yield take(ActiveMinutesTypes.GET_ACTIVE_MINUTES_SUCCESS)
      Toast.show({
        text1: I18n.t('ActiveMinutes.promptNewTarget.updated.title'),
        text2: I18n.t('ActiveMinutes.promptNewTarget.updated.copy', {
          dailyGoal: activeMinutes.daily_goal,
        }),
      })
    }
  } catch (e) {
    Toast.show({
      text1: I18n.t('ActiveMinutes.changeTarget.updateErrorAlert.title'),
      text2: I18n.t('ActiveMinutes.changeTarget.updateErrorAlert.subtitle'),
    })
    yield put(ActiveMinutesActions.updateWeeklyTargetFailure())
    logger.error('Failed to update weekly Active Minutes target', { changeType, target }, e)
  }
}

export function* setConfettiShown() {
  try {
    const ActiveMinutesService = yield* call(createActiveMinutes)

    yield* call(ActiveMinutesService.setConfettiShown, {
      currentDate: moment().format(I18n.t('DateFormat')),
    })

    yield put(ActiveMinutesActions.setConfettiShownSuccess())
    yield put(ActiveMinutesActions.getActiveMinutes())
  } catch (e) {
    yield put(ActiveMinutesActions.setConfettiShownFailure())
    logger.error('Failed to set Active Minutes confetti shown', {}, e)
  }
}

export function* getConnectedTrackers() {
  try {
    const ActiveMinutesService = yield* call(createActiveMinutes)

    // @ts-expect-error The @dialogue/active-minutes model is the source of truth
    const data: ResourceListTracker = yield* call(ActiveMinutesService.getTrackers)

    const mergedSetOfAllSupportedActivities = data.data?.reduce((acc, tracker) => {
      const trackerActivities = supportedTrackerActivities[tracker.provider as TrackerName] || []
      trackerActivities.forEach((activity) => {
        acc.add(activity)
      })
      return acc
    }, new Set())
    const supportedActivities = Array.from(mergedSetOfAllSupportedActivities)

    yield put(
      ActiveMinutesActions.getConnectedTrackersSuccess(
        // edge case to be probably removed when backend is finished
        // to resolve duplicate trackers to pick only the latest updated
        filterLatestConnections(data.data),
        supportedActivities
      )
    )
  } catch (e) {
    yield put(ActiveMinutesActions.getConnectedTrackersFailure())
    logger.error('Failed to get Active Minutes connected trackers', {}, e)
  }
}

const getRedirectUrls = (provider: string) => {
  const baseURL = Config.HEALTH_TRACKER_SETTINGS_SCREEN_DEEP_LINK_URL
  const providerQuery = `provider=${encodeURIComponent(provider)}`
  const authSuccessRedirectUrl = `${baseURL}?${providerQuery}&success=true`
  const authFailureRedirectUrl = `${baseURL}?&error=true`

  return {
    authSuccessRedirectUrl,
    authFailureRedirectUrl,
  }
}

export function* processAPIConnectionRequest({ provider }: { provider: string }) {
  const ActiveMinutesService = yield* call(createActiveMinutes)

  const data = yield* call(ActiveMinutesService.authenticateTracker, {
    provider,
    ...getRedirectUrls(provider),
  })

  yield* call((url) => Linking.openURL(url), data.data.auth_url)
}

export function* processSDKConnectionRequest({ provider }: { provider: string }) {
  yield* call(initTerraConnection, { trackerName: provider, passThroughErrors: true })
}

// process a request for a new connection
// outcome for API providers:
//   a browser tab opens with the auth url,
//   a user will come back by a success/error deeplink to HealthTrackerSettingsScreen with set params
// outcome for SDK providers:
//   init a connection via Terra SDK
//   if the connection is successfull
//   success state would propagate to the HealthTrackerSettingsScreen params as well
// for both options the screen would communicate the result to the user via a banner

export function* requestNewConnection({ provider }: { provider: string }) {
  try {
    const isSupported = supportedTrackers.includes(provider as TrackerName)
    const isAPIProvider = !SDK_TRACKERS.includes(provider as TrackerName)
    if (!isSupported) {
      throw new Error(CONNECTION_ERRORS.PROVIDER_NOT_SUPPORTED)
    }

    const handler = isAPIProvider ? processAPIConnectionRequest : processSDKConnectionRequest
    yield* call(handler, { provider })

    yield put(ActiveMinutesActions.requestNewConnectionSuccess(!isAPIProvider))
  } catch (e) {
    // @ts-expect-error FIXME: error is not guaranteed to be an Error instance
    // Consider if we really need to put anything in the store here, if not remove the action arg
    yield put(ActiveMinutesActions.requestNewConnectionFailure(e?.message || e?.name))
    logger.error('Failed to request new Active Minutes connection', { provider }, e)
  }
}

export function* removeConnection({ id }: { id: string }) {
  try {
    const ActiveMinutesService = yield* call(createActiveMinutes)

    yield* call(ActiveMinutesService.deauthenticateTracker, { trackerId: id })

    yield put(ActiveMinutesActions.removeConnectionSuccess())
    yield put(ActiveMinutesActions.getConnectedTrackers())
  } catch (e) {
    // @ts-expect-error FIXME: error is not guaranteed to be an Error instance
    // Consider if we really need to put anything in the store here, if not remove the action arg
    yield put(ActiveMinutesActions.removeConnectionFailure(e?.message || e?.name))
    logger.error('Failed to remove Active Minutes connection', { connectionId: id }, e)
    Toast.show({
      text1: I18n.t('HealthTrackerSettings.error.disconnect'),
    })
  }
}

export function* getChallenges() {
  try {
    const ActiveMinutesService = yield* call(createActiveMinutes)

    // @ts-expect-error The @dialogue/active-minutes model is the source of truth
    const { data }: CollectionChallenge = yield* call(ActiveMinutesService.getChallenges)
    const categorized = categorizeChallenges(data)

    yield put(
      ActiveMinutesActions.getChallengesSuccess(categorized.joined, categorized.newAndUpcoming)
    )
  } catch (e) {
    yield put(ActiveMinutesActions.getChallengesFailure())
    logger.error('Failed to get Active Minutes challenges', {}, e)
  }
}

export function* getMyCompletedChallenges() {
  try {
    const ActiveMinutesService = yield* call(createActiveMinutes)

    const { data } = yield* call(ActiveMinutesService.getCompletedChallenges)

    const completedWithinLastYear = data?.filter((challenge) => {
      return moment(challenge?.ends_at).isAfter(moment().subtract(1, 'years'))
    })

    yield put(
      ActiveMinutesActions.getMyCompletedChallengesSuccess(
        completedWithinLastYear.length === 0 ? data : completedWithinLastYear
      )
    )
  } catch (e) {
    yield put(ActiveMinutesActions.getMyCompletedChallengesFailure())
    logger.error('Failed to get Active Minutes completed challenges', {}, e)
  }
}

export function* getSingleChallenge({ id }: { id: string }) {
  try {
    const ActiveMinutesService = yield* call(createActiveMinutes)

    // @ts-expect-error The @dialogue/active-minutes model is the source of truth
    const challenge: ResourceChallenge = yield* call(ActiveMinutesService.getChallenge, {
      challengeId: id,
    })

    yield put(ActiveMinutesActions.getSingleChallengeSuccess(challenge.data))
  } catch (e) {
    yield put(ActiveMinutesActions.getSingleChallengeFailure())
    logger.error('Failed to get Active Minutes challenge', { challengeId: id }, e)
  }
}

export function* joinChallenge({ id }: { id: string }) {
  try {
    const ActiveMinutesService = yield* call(createActiveMinutes)

    yield* call(ActiveMinutesService.joinChallenge, { challengeId: id })

    yield put(ActiveMinutesActions.joinChallengeSuccess())
    yield put(ActiveMinutesActions.getSingleChallenge(id))
    yield put(ActiveMinutesActions.getChallenges())
  } catch (e) {
    yield put(ActiveMinutesActions.joinChallengeFailure())
    logger.error('Failed to join Active Minutes challenge', { challengeId: id }, e)
  }
}

export function* getChallengeLeaderboard({ challengeId }: { challengeId: string }) {
  try {
    const ActiveMinutesService = yield* call(createActiveMinutes)

    const leaderboard = yield* call(ActiveMinutesService.getLeaderboard, { challengeId })

    yield put(ActiveMinutesActions.getChallengeLeaderboardSuccess(leaderboard.data))
  } catch (e) {
    yield put(ActiveMinutesActions.getChallengeLeaderboardFailure())
    logger.error('Failed to get Active Minutes challenge leaderboard', { challengeId }, e)
  }
}

export function* leaveChallenge({ id }: { id: string }) {
  try {
    const ActiveMinutesService = yield* call(createActiveMinutes)

    yield* call(ActiveMinutesService.leaveChallenge, { challengeId: id })

    yield put(ActiveMinutesActions.leaveChallengeSuccess())
    yield put(ActiveMinutesActions.getSingleChallenge(id))
    yield put(ActiveMinutesActions.getChallenges())
  } catch (e) {
    yield put(ActiveMinutesActions.leaveChallengeFailure())
    logger.error('Failed to leave Active Minutes challenge', { challengeId: id }, e)
  }
}

export function* handleAppleHealthReconnection() {
  // Ensure we have the latest connected trackers
  yield put(ActiveMinutesActions.getConnectedTrackers())
  yield take([
    ActiveMinutesTypes.GET_CONNECTED_TRACKERS_SUCCESS,
    ActiveMinutesTypes.GET_CONNECTED_TRACKERS_FAILURE,
  ])

  const { data } = yield* select(connectedTrackersState)

  const appleTracker = data?.find(
    (tracker) => tracker['provider'] === ActiveMinutes.Types.TrackerName.AppleHealth
  )

  if (appleTracker) {
    yield put(
      ActiveMinutesActions.removeConnection(
        ActiveMinutes.Types.TrackerName.AppleHealth,
        appleTracker['id']
      )
    )
    yield* call(Nav.navigate, 'healthTrackerSettings')
  }
}
